import { useEffect, useState, useRef } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Button, CircularProgress, TabGroup, SkinTypeBadge } from '@/components/ui'
import { RadarChart } from '@/components/ui/RadarChart'
import { MetricCardLarge, TopConcernCard } from '@/components/report/MetricCard'
import { MetricDetailSheet } from '@/components/report/MetricDetailSheet'
import { ExpandableZoneBreakdown } from '@/components/report/ZoneBreakdown'
import { WeatherCard } from '@/components/report/WeatherCard'
import { KBeautyGuide } from '@/components/report/KBeautyGuide'
import { RetakeFromReportCard } from '@/components/report/RetakeFromReportCard'
import { EducationalLoader } from '@/components/analysis/EducationalLoader'
import { useAnalysisStore } from '@/store/analysisStore'
import { useReportStore } from '@/store/reportStore'
import { useSurveyStore } from '@/store/surveyStore'
import { useFullSurveyStore } from '@/store/fullSurveyStore'
import { useAuthStore } from '@/store/authStore'
import { useToastStore } from '@/store/toastStore'
import { signOut as firebaseSignOut } from '@/lib/firebase'
import { useBackPrevent } from '@/hooks/useBackPrevent'
import { trackFunnelEvent, trackEngagementEvent } from '@/services/analytics'
import { analyzeSkinPhoto, scoresToConcerns } from '@/services/perfectcorp'
import { photoToFile, retrievePhoto } from '@/services/photoStorage'
import { getWeatherInsight, type WeatherInsight } from '@/services/weatherService'
import { mockEnhancedAnalysis } from '@/data/mockAnalysis'
import { TEST_PHOTO_ID, TEST_FULL_RESULTS } from '@/lib/testFixtures'
import { debugLog } from '@/lib/logger'
import { haptic } from '@/lib/haptics'
import { METRIC_DISPLAY_ORDER, METRIC_CATEGORIES, METRIC_INFO } from '@/data/metricExplanations'
import { getBenchmark } from '@/data/ageBenchmarks'
import type { MetricKey, MetricDetail, MetricCategory, SkinType } from '@/types/analysis'
import { getMetricStatus } from '@/types/analysis'
import type { PriceTier, RecommendedStep } from '@/types/product'
import type { SkinCondition, SkinType as SurveySkinType } from '@/types/survey'
import { getProductTags, CONCERN_COLORS, type UserConcerns } from '@/utils/productTags'

// Map survey skin conditions to corresponding MetricKey
const SURVEY_CONDITION_TO_METRIC: Partial<Record<SkinCondition, MetricKey>> = {
  acne: 'acne',
  dryness: 'moisture',
  oiliness: 'oiliness',
  sensitivity: 'redness',
  hyperpigmentation: 'spots',
  rosacea: 'redness',
  eczema: 'moisture',
  // 'none' has no mapping
}

// Display names for survey conditions
const SURVEY_CONDITION_LABELS: Record<SkinCondition, string> = {
  acne: 'Acne',
  dryness: 'Dryness',
  oiliness: 'Oiliness',
  sensitivity: 'Sensitivity',
  hyperpigmentation: 'Hyperpigmentation',
  rosacea: 'Rosacea',
  eczema: 'Eczema',
  none: 'None',
}

const BUDGET_TABS: { id: PriceTier; label: string }[] = [
  { id: 'affordable', label: 'Affordable' },
  { id: 'mainstream', label: 'Standard' },
  { id: 'premium', label: 'Premium' },
]

// Get user age (default to 30 if not provided)
function getUserAge(age: number | null): number {
  return age ?? 30
}

// Group metrics by category
function groupMetricsByCategory(metrics: Record<MetricKey, MetricDetail>) {
  const groups: Record<MetricCategory, MetricDetail[]> = {
    skinHealth: [],
    agingSigns: [],
    eyeArea: [],
  }

  METRIC_DISPLAY_ORDER.forEach((key) => {
    const metric = metrics[key]
    if (metric) {
      groups[metric.category].push(metric)
    }
  })

  return groups
}

export function ReportPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { analysisResults, setAnalysisResults, photoId, hasHDResults, clearForRetake, saveToBackend } = useAnalysisStore()
  const { shortSurvey } = useSurveyStore()
  const { fullSurvey } = useFullSurveyStore()
  const { isAuthenticated, hasPaid, demoMode } = useAuthStore()
  const showToast = useToastStore((state) => state.showToast)

  // SECURITY: Block rendering immediately if user hasn't completed the flow
  // This prevents showing any content before the useEffect redirect fires
  const isAuthorized = analysisResults && hasHDResults() && (demoMode || (isAuthenticated && hasPaid))

  // Show welcome toast if redirected from another page (once per session)
  useEffect(() => {
    const state = location.state as { showWelcomeToast?: boolean } | null
    const toastKey = 'k-skin-report-welcome-shown'
    if (state?.showWelcomeToast && !sessionStorage.getItem(toastKey)) {
      sessionStorage.setItem(toastKey, 'true')
      showToast('Welcome back! Here is your full report.', 'info', 5000)
      window.history.replaceState({}, document.title)
    }
  }, [location.state, showToast])

  // Route guard: redirect if not authenticated/paid or no analysis results
  useEffect(() => {
    if (!analysisResults) {
      navigate('/survey', { replace: true })
    } else if (!demoMode && (!isAuthenticated || !hasPaid)) {
      navigate('/payment', { replace: true })
    } else if (!hasHDResults() && !demoMode) {
      // User paid but hasn't completed HD analysis - send to full survey
      navigate('/full-survey', { replace: true })
    }
  }, [analysisResults, isAuthenticated, hasPaid, hasHDResults, demoMode, navigate])

  // Don't render anything until authorized (prevents flash of content)
  if (!isAuthorized) {
    return null
  }

  // Track report viewed (once per session)
  const hasTrackedView = useRef(false)
  useEffect(() => {
    if (!hasTrackedView.current && analysisResults) {
      hasTrackedView.current = true
      trackFunnelEvent.reportViewed()
    }
  }, [analysisResults])

  // Prevent back button navigation
  useBackPrevent("Your full skin report is ready! Explore your results here.")

  // HD analysis state
  const [isRunningHDAnalysis, setIsRunningHDAnalysis] = useState(false)
  const [hdAnalysisProgressPercent, setHdAnalysisProgressPercent] = useState(0)
  const [hdAnalysisError, setHdAnalysisError] = useState<string | null>(null)
  const {
    selectedBudget,
    recommendedRoutine,
    isLoadingRoutine,
    routineError,
    setSelectedBudget,
    loadRealRoutines,
    getCurrentRoutineCost,
  } = useReportStore()

  const [mainTab, setMainTab] = useState<'analysis' | 'routine' | 'products'>('products') // Products tab first

  // One-time retake option state (persisted in localStorage)
  const [retakeUsed, setRetakeUsed] = useState(() => localStorage.getItem('k-skin-retake-used') === 'true')

  // Handle one-time photo retake from Report page
  const handleReportRetake = async () => {
    haptic('light')
    // Mark retake as used
    localStorage.setItem('k-skin-retake-used', 'true')
    setRetakeUsed(true)

    // Save current analysis to backend before clearing (non-blocking)
    await saveToBackend('full')

    // Clear HD data but keep basic metrics
    clearForRetake()

    // Navigate to photo capture (will go to HD analysis after)
    navigate('/photo', { state: { isReportRetake: true } })
  }

  // Track scroll positions for each tab
  const scrollPositions = useRef<Record<string, number>>({ analysis: 0, routine: 0, products: 0 })

  // Handle tab change - save current scroll position and restore the new tab's position
  const handleTabChange = (newTab: 'analysis' | 'routine' | 'products') => {
    // Track tab change
    trackEngagementEvent.tabChanged(newTab)
    // Save current scroll position
    scrollPositions.current[mainTab] = window.scrollY
    // Change tab
    setMainTab(newTab)
    // Restore scroll position for new tab (after render)
    setTimeout(() => {
      window.scrollTo(0, scrollPositions.current[newTab])
    }, 0)
  }
  const [selectedMetric, setSelectedMetric] = useState<MetricDetail | null>(null)
  const [isDetailOpen, setIsDetailOpen] = useState(false)
  const [weatherData, setWeatherData] = useState<WeatherInsight | null>(null)

  // Fetch weather data based on ZIP code from full survey
  useEffect(() => {
    async function fetchWeather() {
      if (fullSurvey.zipCode) {
        try {
          const weather = await getWeatherInsight(fullSurvey.zipCode)
          setWeatherData(weather)
        } catch (error) {
          console.error('Failed to fetch weather data:', error)
        }
      }
    }
    fetchWeather()
  }, [fullSurvey.zipCode])

  // Load product recommendations when page loads (always use full/targeted routine)
  useEffect(() => {
    if (!recommendedRoutine && !isLoadingRoutine && !routineError) {
      loadRealRoutines('targeted')
    }
  }, [recommendedRoutine, isLoadingRoutine, routineError, loadRealRoutines])

  // If no analysis results, fall back to mock for demo purposes
  useEffect(() => {
    if (!analysisResults) {
      setAnalysisResults(mockEnhancedAnalysis as any)
    }
  }, [analysisResults, setAnalysisResults])

  // HD Analysis Trigger: Run HD analysis if we have a stored photo but no HD results yet
  useEffect(() => {
    const runHDAnalysis = async () => {
      // Skip if already running, already have HD results, or no photo stored
      if (isRunningHDAnalysis || hasHDResults() || !photoId) {
        return
      }

      console.log('[ReportPage] Starting HD analysis for stored photo:', photoId)
      setIsRunningHDAnalysis(true)
      setHdAnalysisError(null)
      setHdAnalysisProgressPercent(5)

      try {
        // TEST MODE: Skip API call and use pre-saved test data
        if (photoId === TEST_PHOTO_ID) {
          console.log('[ReportPage] Test mode detected - using pre-saved HD results')
          setHdAnalysisProgressPercent(50)

          // Simulate a short delay for UX
          await new Promise(resolve => setTimeout(resolve, 1500))

          setHdAnalysisProgressPercent(90)

          // Use test results with current photo URL
          setAnalysisResults({
            ...TEST_FULL_RESULTS,
            photoUrl: analysisResults?.photoUrl || '',
            analyzedAt: new Date().toISOString(),
          })

          console.log('[ReportPage] Test HD results stored successfully')

          // Save full analysis to backend (non-blocking)
          useAnalysisStore.getState().saveToBackend('full')

          setIsRunningHDAnalysis(false)
          setHdAnalysisProgressPercent(100)
          return
        }

        // NORMAL MODE: Call YCE API
        // Retrieve photo from S3
        const file = await photoToFile(photoId, 'photo.jpg')
        console.log('[ReportPage] Photo retrieved, size:', Math.round(file.size / 1024), 'KB')
        setHdAnalysisProgressPercent(15)

        // Run HD analysis
        setHdAnalysisProgressPercent(25)
        const apiResult = await analyzeSkinPhoto(file, (stage) => {
          // Update progress based on stage
          if (stage.includes('Uploading')) setHdAnalysisProgressPercent(30)
          else if (stage.includes('Processing')) setHdAnalysisProgressPercent(50)
          else if (stage.includes('Extracting')) setHdAnalysisProgressPercent(70)
          else if (stage.includes('Complete')) setHdAnalysisProgressPercent(90)
        }, 'full')

        console.log('[ReportPage] HD analysis complete:', apiResult.skinAge, apiResult.skinScore)

        // Convert to concern scores
        const concerns = scoresToConcerns(apiResult.scores)

        // Derive skin type
        const deriveSkinType = (): SkinType => {
          const oiliness = concerns.oiliness
          if (oiliness >= 70) return 'oily'
          if (oiliness >= 40) return 'combination'
          if (oiliness <= 20) return 'dry'
          return 'normal'
        }

        const skinType: SkinType =
          shortSurvey.skinType && shortSurvey.skinType !== 'not-sure'
            ? shortSurvey.skinType as SkinType
            : deriveSkinType()

        // Helper to find heatmaps
        const allHeatmapKeys = Object.keys(apiResult.heatmaps)
        const findHeatmap = (baseName: string): string => {
          const variations = [
            baseName, `${baseName}_whole`, `${baseName}_all`,
            `${baseName}_output`, `${baseName}_output_whole`, `${baseName}_output_all`,
            `hd_${baseName}`, `hd_${baseName}_whole`, `hd_${baseName}_all`,
          ]
          for (const key of variations) {
            if (apiResult.heatmaps[key]) return apiResult.heatmaps[key]
          }
          const partial = allHeatmapKeys.find(k => k.toLowerCase().includes(baseName.toLowerCase()))
          return partial ? apiResult.heatmaps[partial] : ''
        }

        // Zone scores (higher = better skin condition)
        const wrinkleZoneScores = apiResult.scores.wrinkleZones ? {
          forehead: apiResult.scores.wrinkleZones.forehead,
          glabellar: apiResult.scores.wrinkleZones.glabellar,
          crowfeet: apiResult.scores.wrinkleZones.crowfeet,
          periocular: apiResult.scores.wrinkleZones.periocular,
          nasolabial: apiResult.scores.wrinkleZones.nasolabial,
          marionette: apiResult.scores.wrinkleZones.marionette,
        } : undefined

        const poreZoneScores = apiResult.scores.poreZones ? {
          forehead: apiResult.scores.poreZones.forehead,
          nose: apiResult.scores.poreZones.nose,
          cheek: apiResult.scores.poreZones.cheek,
        } : undefined

        // Update analysis results with HD data (ALL 14 metrics)
        setAnalysisResults({
          skinAge: apiResult.skinAge,
          skinScore: apiResult.skinScore,
          skinType,
          metrics: {
            // Core metrics
            pores: concerns.pores,
            texture: concerns.texture,
            pigmentation: concerns.spots,
            redness: concerns.redness,
            wrinkles: concerns.wrinkles,
            brightness: apiResult.scores.radiance, // Legacy field
            oiliness: concerns.oiliness,
            acne: concerns.acne,
            // HD-only metrics
            moisture: concerns.moisture,
            firmness: concerns.firmness,
            radiance: concerns.radiance,
            spots: concerns.spots,
            darkCircles: concerns.darkCircles,
            eyeBags: concerns.eyeBags,
            droopyEyelidUpper: concerns.droopyEyelidUpper,
            droopyEyelidLower: concerns.droopyEyelidLower,
          },
          heatmaps: {
            pores: findHeatmap('pore'),
            texture: findHeatmap('texture'),
            pigmentation: findHeatmap('age_spot') || findHeatmap('spot'),
            wrinkles: findHeatmap('wrinkle'),
            redness: findHeatmap('redness'),
            acne: findHeatmap('acne'),
            oiliness: findHeatmap('oiliness'),
            moisture: findHeatmap('moisture'),
            firmness: findHeatmap('firmness'),
            radiance: findHeatmap('radiance'),
            darkCircles: findHeatmap('dark_circle'),
            eyeBags: findHeatmap('eye_bag'),
            droopyEyelidUpper: findHeatmap('droopy_upper_eyelid'),
            droopyEyelidLower: findHeatmap('droopy_lower_eyelid'),
            // Zone-specific heatmaps (HD only)
            wrinkleZones: apiResult.zoneHeatmaps?.wrinkleZones,
            poreZones: apiResult.zoneHeatmaps?.poreZones,
          },
          photoUrl: analysisResults?.photoUrl || '',
          analyzedAt: new Date().toISOString(),
          // Zone-specific scores (HD only)
          wrinkleZoneScores,
          poreZoneScores,
        })

        console.log('[ReportPage] HD results stored successfully')

        // Save full analysis to backend (non-blocking)
        // Use getState() to access the updated store after setAnalysisResults
        useAnalysisStore.getState().saveToBackend('full')
      } catch (error) {
        console.error('[ReportPage] HD analysis failed:', error)
        setHdAnalysisError(error instanceof Error ? error.message : 'Analysis failed')
      } finally {
        setIsRunningHDAnalysis(false)
      }
    }

    runHDAnalysis()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photoId]) // Only run when photoId changes

  // State for user photo loaded from S3
  const [userPhotoUrl, setUserPhotoUrl] = useState<string>('')

  // Load user photo from S3 when photoId is available
  useEffect(() => {
    const loadUserPhoto = async () => {
      if (!photoId || userPhotoUrl) return
      try {
        const blob = await retrievePhoto(photoId)
        const url = URL.createObjectURL(blob)
        setUserPhotoUrl(url)
      } catch (error) {
        console.warn('[ReportPage] Could not load user photo:', error)
      }
    }
    loadUserPhoto()
  }, [photoId, userPhotoUrl])

  const userAge = getUserAge(shortSurvey.age)

  // Build real metrics from API data (not mock)
  // Maps our metric keys to the heatmap keys from the API (string values only, not zone objects)
  type SimpleHeatmapKey = 'pores' | 'texture' | 'pigmentation' | 'wrinkles' | 'redness' |
    'acne' | 'oiliness' | 'moisture' | 'firmness' | 'radiance' |
    'darkCircles' | 'eyeBags' | 'droopyEyelidUpper' | 'droopyEyelidLower'

  const heatmapKeyMap: Record<MetricKey, SimpleHeatmapKey> = {
    pores: 'pores',
    texture: 'texture',
    wrinkles: 'wrinkles',
    spots: 'pigmentation',
    redness: 'redness',
    acne: 'acne',
    oiliness: 'oiliness',
    moisture: 'moisture',
    firmness: 'firmness',
    radiance: 'radiance',
    darkCircles: 'darkCircles',
    eyeBags: 'eyeBags',
    droopyEyelidUpper: 'droopyEyelidUpper',
    droopyEyelidLower: 'droopyEyelidLower',
  }

  // Map our metric keys to the SkinMetrics keys
  const metricsKeyMap: Record<MetricKey, keyof NonNullable<typeof analysisResults>['metrics']> = {
    pores: 'pores',
    texture: 'texture',
    wrinkles: 'wrinkles',
    spots: 'spots',
    redness: 'redness',
    acne: 'acne',
    oiliness: 'oiliness',
    moisture: 'moisture',
    firmness: 'firmness',
    radiance: 'radiance',
    darkCircles: 'darkCircles',
    eyeBags: 'eyeBags',
    droopyEyelidUpper: 'droopyEyelidUpper',
    droopyEyelidLower: 'droopyEyelidLower',
  }

  // Create MetricDetail objects from real API data
  const createMetricFromAPI = (key: MetricKey): MetricDetail => {
    const info = METRIC_INFO[key]
    const metricsKey = metricsKeyMap[key]
    const heatmapKey = heatmapKeyMap[key]

    // Get real score from API (0-100, higher = worse)
    const realScore = analysisResults?.metrics?.[metricsKey]
    const mockScore = mockEnhancedAnalysis.metrics[key].score
    const score = realScore ?? mockScore

    // Debug: Log when using mock data
    if (realScore === undefined) {
      debugLog(`[ReportPage] ⚠️ USING MOCK DATA for ${key}: ${mockScore} (real value missing)`)
    } else {
      debugLog(`[ReportPage] ✓ Using real data for ${key}: ${realScore}`)
    }

    const ageAverage = getBenchmark(userAge, key)

    return {
      key,
      name: info.name,
      category: info.category,
      score,
      ageAverage,
      status: getMetricStatus(score, ageAverage),
      heatmapUrl: (analysisResults?.heatmaps?.[heatmapKey] as string) || '',
      zones: key === 'wrinkles' ? analysisResults?.wrinkleZoneScores :
             key === 'pores' ? analysisResults?.poreZoneScores : undefined,
    }
  }

  // Build all metrics from API data
  const realMetrics = METRIC_DISPLAY_ORDER.reduce((acc, key) => {
    acc[key] = createMetricFromAPI(key)
    return acc
  }, {} as Record<MetricKey, MetricDetail>)

  // Get top 3 concerns (lowest scores = worst areas needing attention)
  const topConcernKeys = Object.entries(realMetrics)
    .sort((a, b) => a[1].score - b[1].score)
    .slice(0, 3)
    .map(([key]) => key as MetricKey)

  // Find survey concerns not in top 3 AI concerns
  const surveyConcerns = (shortSurvey.skinConditions ?? [])
    .filter((condition): condition is Exclude<SkinCondition, 'none'> =>
      condition !== 'none' && SURVEY_CONDITION_TO_METRIC[condition] !== undefined
    )
    .map(condition => ({
      condition,
      metricKey: SURVEY_CONDITION_TO_METRIC[condition]!,
      label: SURVEY_CONDITION_LABELS[condition],
    }))

  // Survey concerns not covered by top 3 AI concerns
  const additionalSurveyConcerns = surveyConcerns.filter(
    sc => !topConcernKeys.includes(sc.metricKey)
  )

  // Final results object using real API data
  const results = {
    skinAge: analysisResults?.skinAge ?? mockEnhancedAnalysis.skinAge,
    skinScore: analysisResults?.skinScore ?? mockEnhancedAnalysis.skinScore,
    skinType: analysisResults?.skinType ?? mockEnhancedAnalysis.skinType,
    photoUrl: userPhotoUrl || analysisResults?.photoUrl || mockEnhancedAnalysis.photoUrl,
    metrics: realMetrics,
    topConcerns: topConcernKeys,
    analyzedAt: analysisResults?.analyzedAt ?? new Date().toISOString(),
  }
  const ageDiff = results.skinAge - userAge
  const costs = getCurrentRoutineCost()

  // User's concerns from HD analysis for personalized hashtags (top 3 only)
  const userConcerns: UserConcerns = topConcernKeys.reduce((acc, key) => {
    acc[key] = analysisResults?.metrics?.[key] ?? 0
    return acc
  }, {} as UserConcerns)

  // Group metrics for display
  const groupedMetrics = groupMetricsByCategory(results.metrics)

  // Flat array of all metrics for detail sheet navigation
  const allMetricsArray = METRIC_DISPLAY_ORDER.map(key => results.metrics[key])

  const handleMetricClick = (metric: MetricDetail) => {
    setSelectedMetric(metric)
    setIsDetailOpen(true)
  }

  const handleMetricNavigate = (metric: MetricDetail) => {
    setSelectedMetric(metric)
  }

  // Show educational loader while HD analysis is running
  if (isRunningHDAnalysis) {
    return (
      <EducationalLoader
        progress={hdAnalysisProgressPercent}
        isComplete={hdAnalysisProgressPercent >= 100}
      />
    )
  }

  // Show error screen if HD analysis failed
  if (hdAnalysisError) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-red-100 flex items-center justify-center">
            <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>

          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Analysis Failed
          </h2>
          <p className="text-gray-600 mb-6">
            {hdAnalysisError}
          </p>

          <div className="space-y-3">
            <Button
              onClick={() => {
                setHdAnalysisError(null)
                // Trigger re-analysis by resetting the state
                window.location.reload()
              }}
              className="w-full"
            >
              Try Again
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/photo')}
              className="w-full"
            >
              Retake Photo
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FFF8F5] via-[#FFFEF1] to-[#F0FAFA] relative overflow-x-hidden">
      {/* Decorative sparkles */}
      <motion.svg
        className="absolute top-20 left-4 z-0"
        width={14}
        height={14}
        viewBox="0 0 24 24"
        fill="#FF8C33"
        animate={{ scale: [1, 1.3, 1], opacity: [0.5, 0.8, 0.5], rotate: [0, 15, 0] }}
        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
      >
        <path d="M12 0L13.5 9L22 12L13.5 15L12 24L10.5 15L2 12L10.5 9L12 0Z" />
      </motion.svg>
      <motion.svg
        className="absolute top-32 right-6 z-0"
        width={10}
        height={10}
        viewBox="0 0 24 24"
        fill="#46C0C6"
        animate={{ scale: [1, 1.3, 1], opacity: [0.5, 0.8, 0.5], rotate: [0, 15, 0] }}
        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
      >
        <path d="M12 0L13.5 9L22 12L13.5 15L12 24L10.5 15L2 12L10.5 9L12 0Z" />
      </motion.svg>
      <motion.svg
        className="absolute top-80 left-8 z-0"
        width={12}
        height={12}
        viewBox="0 0 24 24"
        fill="#F596FA"
        animate={{ scale: [1, 1.3, 1], opacity: [0.5, 0.8, 0.5], rotate: [0, 15, 0] }}
        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut", delay: 1 }}
      >
        <path d="M12 0L13.5 9L22 12L13.5 15L12 24L10.5 15L2 12L10.5 9L12 0Z" />
      </motion.svg>

      {/* Header with Logo and Tabs */}
      <header className="sticky top-0 z-40 bg-white/60 backdrop-blur-md border-b border-gray-100/50">
        {/* Logo Row - Centered */}
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-center">
          <Link
            to="/"
            className="text-[22px]"
            style={{ fontFamily: "'Oleo Script', cursive" }}
          >
            <span className="text-[#688192]">K-Beauty</span>
            <span className="text-[#46C0C6]"> AI</span>
          </Link>
        </div>

        {/* Full-width Tab Navigation */}
        <div className="flex w-full p-2 gap-2 bg-white/95 backdrop-blur-sm">
          <button
            onClick={() => handleTabChange('products')}
            className={`flex-1 py-2.5 text-sm font-semibold rounded-xl transition-all ${
              mainTab === 'products'
                ? 'bg-[#46C0C6] text-white shadow-md'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            ✨ Products
          </button>
          <button
            onClick={() => handleTabChange('analysis')}
            className={`flex-1 py-2.5 text-sm font-semibold rounded-xl transition-all ${
              mainTab === 'analysis'
                ? 'bg-[#46C0C6] text-white shadow-md'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            📊 Analysis
          </button>
          <button
            onClick={() => handleTabChange('routine')}
            className={`flex-1 py-2.5 text-sm font-semibold rounded-xl transition-all ${
              mainTab === 'routine'
                ? 'bg-[#46C0C6] text-white shadow-md'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            🌸 K-Routine
          </button>
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-2xl mx-auto px-4 py-4 pb-24">

        {/* Routine Tab Content */}
        {mainTab === 'routine' && (
          <>
            {/* K-Beauty Guide Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 }}
              className="mb-6"
            >
              <KBeautyGuide />
            </motion.div>

            {/* Ingredient Tips */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 mb-6 shadow-sm border border-white/50"
            >
              <h2 className="text-lg font-semibold text-[#075D61] mb-4" style={{ fontFamily: "'Anybody', sans-serif" }}>
                Ingredient Recommendations
              </h2>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                    <svg className="w-4 h-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Look for these:</p>
                    <p className="text-sm text-gray-600">
                      Niacinamide, Hyaluronic Acid, Centella Asiatica, Snail Mucin
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                    <svg className="w-4 h-4 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Avoid these:</p>
                    <p className="text-sm text-gray-600">
                      Denatured Alcohol, Heavy Fragrances, Harsh Sulfates
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Weather-based Recommendations */}
            {weatherData && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 }}
                className="mb-6"
              >
                <WeatherCard weather={weatherData} />
              </motion.div>
            )}
          </>
        )}

        {/* Analysis Tab Content */}
        {mainTab === 'analysis' && (
          <>
            {/* Section Title */}
            <motion.h2
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-2xl font-bold text-[#075D61] mb-4"
              style={{ fontFamily: "'Anybody', sans-serif" }}
            >
              Your Full Skin Analysis
            </motion.h2>

            {/* Overview Cards */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="grid grid-cols-3 gap-3 mb-6"
            >
              {/* Skin Score */}
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 shadow-sm text-center border border-white/50">
                <CircularProgress value={results.skinScore} size="sm" strokeWidth={6} />
                <p className="text-xs text-gray-500 mt-2 font-medium">Skin Score</p>
              </div>

              {/* Skin Age */}
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 shadow-sm text-center border border-white/50">
                <div className="h-20 flex items-center justify-center">
                  <div>
                    <p className="text-3xl font-bold text-[#075D61]">{results.skinAge}</p>
                    <p className={`text-xs font-medium ${ageDiff <= 0 ? 'text-green-500' : 'text-orange-500'}`}>
                      {ageDiff <= 0 ? `${Math.abs(ageDiff)} yrs younger` : `${ageDiff} yrs older`}
                    </p>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-2 font-medium">Skin Age</p>
              </div>

              {/* Skin Type */}
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 shadow-sm text-center border border-white/50">
                <div className="h-20 flex items-center justify-center">
                  <SkinTypeBadge skinType={results.skinType} />
                </div>
                <p className="text-xs text-gray-500 mt-2 font-medium">Skin Type</p>
              </div>
            </motion.div>

        {/* Radar Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-sm mb-6 border border-white/50"
        >
          <h2 className="text-lg font-semibold text-[#075D61] mb-4 text-center" style={{ fontFamily: "'Anybody', sans-serif" }}>
            Skin Balance Overview
          </h2>
          <div className="flex justify-center">
            <RadarChart metrics={results.metrics} size={280} animated={true} />
          </div>
        </motion.div>

        {/* Zone Analysis (HD only - shown when zone scores are available) */}
        {(analysisResults?.wrinkleZoneScores || analysisResults?.poreZoneScores) && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.18 }}
            className="mb-6"
          >
            <h2 className="text-lg font-semibold text-gray-900 mb-3">
              Zone Analysis
            </h2>
            <p className="text-sm text-gray-500 mb-4">
              Detailed breakdown by facial area
            </p>
            <div className="space-y-3">
              {/* Wrinkle zones */}
              {analysisResults?.wrinkleZoneScores && (
                <ExpandableZoneBreakdown
                  title="Wrinkles"
                  icon="〰️"
                  overallScore={analysisResults.metrics.wrinkles}
                  zones={analysisResults.wrinkleZoneScores}
                  zoneHeatmaps={analysisResults.heatmaps?.wrinkleZones as Record<string, string | undefined> | undefined}
                  zoneLabels={{
                    forehead: 'Forehead',
                    glabellar: 'Frown Lines',
                    crowfeet: "Crow's Feet",
                    periocular: 'Eye Area',
                    nasolabial: 'Smile Lines',
                    marionette: 'Mouth Lines',
                  }}
                  photoUrl={results.photoUrl}
                />
              )}
              {/* Pore zones */}
              {analysisResults?.poreZoneScores && (
                <ExpandableZoneBreakdown
                  title="Pores"
                  icon="⚬"
                  overallScore={analysisResults.metrics.pores}
                  zones={analysisResults.poreZoneScores}
                  zoneHeatmaps={analysisResults.heatmaps?.poreZones as Record<string, string | undefined> | undefined}
                  zoneLabels={{
                    forehead: 'Forehead',
                    nose: 'Nose',
                    cheek: 'Cheeks',
                  }}
                  photoUrl={results.photoUrl}
                />
              )}
            </div>
          </motion.div>
        )}

        {/* Top Concerns - Simple cards with emoji, name, score */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="mb-8"
            >
              <h2 className="text-xl font-bold text-[#075D61] mb-2" style={{ fontFamily: "'Anybody', sans-serif" }}>
                Your Top Concerns
              </h2>
              <p className="text-sm text-gray-500 mb-4">
                Areas that need the most attention based on your analysis
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {results.topConcerns.map((key, index) => {
                  const metric = results.metrics[key]
                  return (
                    <TopConcernCard
                      key={key}
                      metric={metric}
                      onClick={() => handleMetricClick(metric)}
                      rank={index + 1}
                    />
                  )
                })}
              </div>

              {/* Additional Survey Concerns - not in top 3 by AI severity */}
              {additionalSurveyConcerns.length > 0 && (
                <div className="mt-6 p-4 bg-blue-50 rounded-xl border border-blue-100">
                  <p className="text-sm text-blue-800 mb-3">
                    Based on your survey responses, we're also monitoring:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {additionalSurveyConcerns.map(({ condition, metricKey, label }) => {
                      const metric = results.metrics[metricKey]
                      const score = metric?.score ?? 0
                      const isLowSeverity = score < 30
                      return (
                        <button
                          key={condition}
                          onClick={() => metric && handleMetricClick(metric)}
                          className="flex items-center gap-2 px-3 py-2 bg-white rounded-lg border border-blue-200 hover:border-blue-400 transition-colors"
                        >
                          <span className="text-sm font-medium text-gray-800">{label}</span>
                          <span className={`text-xs px-1.5 py-0.5 rounded ${
                            isLowSeverity
                              ? 'bg-green-100 text-green-700'
                              : 'bg-amber-100 text-amber-700'
                          }`}>
                            {score}
                          </span>
                        </button>
                      )
                    })}
                  </div>
                  <p className="text-xs text-blue-600 mt-2">
                    Your AI analysis shows these are currently mild, but we'll factor them into your recommendations.
                  </p>
                </div>
              )}
            </motion.div>

            {/* All Metrics by Category - Large 2-column layout */}
            {(Object.entries(groupedMetrics) as [MetricCategory, MetricDetail[]][]).map(
              ([category, metrics], categoryIndex) => (
                <motion.div
                  key={category}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.25 + categoryIndex * 0.05 }}
                  className="mb-8"
                >
                  <div className="flex items-center gap-2 mb-4">
                    <h2 className="text-xl font-bold text-gray-900">
                      {METRIC_CATEGORIES[category].name}
                    </h2>
                    <span className="px-2 py-0.5 bg-gray-100 rounded-full text-sm text-gray-500">
                      {metrics.length}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {metrics.map((metric) => (
                      <MetricCardLarge
                        key={metric.key}
                        metric={metric}
                        photoUrl={results.photoUrl}
                        onClick={() => handleMetricClick(metric)}
                      />
                    ))}
                  </div>
                </motion.div>
              )
            )}

            {/* One-time photo retake option */}
            {!retakeUsed && (
              <div className="mt-8 mb-16">
                <RetakeFromReportCard onRetake={handleReportRetake} />
              </div>
            )}

            </>
        )}

        {/* Products Tab Content */}
        {mainTab === 'products' && (
          <>
            {/* Products Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 }}
              className="mb-6"
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-[#075D61]" style={{ fontFamily: "'Anybody', sans-serif" }}>
                  Your Personalized<br />K-Cosmetics
                </h2>
                <div className="text-sm bg-[#46C0C6]/10 text-[#075D61] px-3 py-1 rounded-full font-semibold">
                  Total: ${costs.total.toFixed(0)}
                </div>
              </div>

              {/* Personalized Intro */}
              <motion.div
                className="relative overflow-hidden rounded-2xl p-5 mb-6 border border-[#46C0C6]/20"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                {/* Animated gradient background */}
                <motion.div
                  className="absolute inset-0 bg-gradient-to-br from-[#ECF9F9] via-[#F0F9FF] to-[#F5F3FF]"
                  animate={{
                    background: [
                      'linear-gradient(135deg, #D4F5F5 0%, #E0F0FF 50%, #EDE9FE 100%)',
                      'linear-gradient(135deg, #EDE9FE 0%, #D4F5F5 50%, #E0F0FF 100%)',
                      'linear-gradient(135deg, #E0F0FF 0%, #EDE9FE 50%, #D4F5F5 100%)',
                      'linear-gradient(135deg, #D4F5F5 0%, #E0F0FF 50%, #EDE9FE 100%)',
                    ],
                  }}
                  transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
                />
                {/* Shimmer effect */}
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent"
                  animate={{ x: ['-100%', '100%'] }}
                  transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut', repeatDelay: 1.5 }}
                />
                <div className="relative z-10">
                <div className="flex items-start gap-3 mb-3">
                  <span className="text-2xl">✨</span>
                  <div>
                    <h3 className="text-base font-semibold text-gray-900 mb-1">Your Personalized K-Beauty Routine</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      Based on your survey responses and AI skin analysis, we've curated the perfect K-beauty products just for you!
                    </p>
                  </div>
                </div>
                <div className="bg-white/60 rounded-xl p-3 mt-3">
                  <p className="text-xs font-medium text-gray-500 mb-2">Your main skin concerns:</p>
                  <div className="flex flex-wrap gap-2">
                    {topConcernKeys.map((key) => {
                      const colors = CONCERN_COLORS[key]
                      return (
                        <span
                          key={key}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium ${
                            colors ? `${colors.bg} ${colors.text}` : 'bg-gray-100 text-gray-700'
                          }`}
                        >
                          #{METRIC_INFO[key]?.name || key}
                        </span>
                      )
                    })}
                    {surveyConcerns.map((sc) => (
                      <span
                        key={sc.condition}
                        className="px-3 py-1.5 bg-purple-100 text-purple-700 rounded-full text-xs font-medium"
                      >
                        #{sc.label}
                      </span>
                    ))}
                  </div>
                </div>
                </div>
              </motion.div>

              {/* Budget Tier Selector */}
              <TabGroup
                tabs={BUDGET_TABS.map((t) => ({
                  id: t.id,
                  label: t.label,
                  sublabel: recommendedRoutine?.totalPrice
                    ? `$${Math.round(recommendedRoutine.totalPrice[t.id])}`
                    : undefined,
                }))}
                selected={selectedBudget}
                onChange={(id) => {
                  trackEngagementEvent.priceTierChanged(id as PriceTier)
                  setSelectedBudget(id as PriceTier)
                }}
                className="mb-6"
              />

              {/* Loading State */}
              {isLoadingRoutine && (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="w-8 h-8 border-2 border-[#46C0C6] border-t-transparent rounded-full animate-spin mb-4" />
                  <p className="text-gray-500 text-sm">Building your personalized routine...</p>
                </div>
              )}

              {/* Error State */}
              {routineError && !isLoadingRoutine && (
                <div className="bg-red-50 rounded-xl p-4 mb-6">
                  <p className="text-red-600 text-sm mb-2">Failed to load recommendations</p>
                  <button
                    onClick={() => loadRealRoutines()}
                    className="text-[#46C0C6] text-sm font-medium hover:underline"
                  >
                    Try Again
                  </button>
                </div>
              )}

              {/* Products Display */}
              {recommendedRoutine && !isLoadingRoutine && (
                <>
                  {/* AM Products */}
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-[#075D61] mb-3 flex items-center gap-2" style={{ fontFamily: "'Anybody', sans-serif" }}>
                      <span className="text-xl">🌅</span> Morning Routine
                      <span className="text-xs text-[#46C0C6] bg-[#46C0C6]/10 px-2 py-1 rounded-full ml-auto">{recommendedRoutine.am.length} steps</span>
                    </h3>
                    <div className="space-y-3">
                      {recommendedRoutine.am.map((step) => (
                        <RecommendedProductCard
                          key={`am-${step.step}`}
                          step={step}
                          selectedTier={selectedBudget}
                          userConcerns={userConcerns}
                          userSkinType={shortSurvey.skinType}
                        />
                      ))}
                    </div>
                  </div>

                  {/* PM Products */}
                  <div>
                    <h3 className="text-lg font-semibold text-[#075D61] mb-3 flex items-center gap-2" style={{ fontFamily: "'Anybody', sans-serif" }}>
                      <span className="text-xl">🌙</span> Evening Routine
                      <span className="text-xs text-[#7182F8] bg-[#7182F8]/10 px-2 py-1 rounded-full ml-auto">{recommendedRoutine.pm.length} steps</span>
                    </h3>
                    <div className="space-y-3">
                      {recommendedRoutine.pm.map((step) => (
                        <RecommendedProductCard
                          key={`pm-${step.step}`}
                          step={step}
                          selectedTier={selectedBudget}
                          userConcerns={userConcerns}
                          userSkinType={shortSurvey.skinType}
                        />
                      ))}
                    </div>
                  </div>
                </>
              )}
            </motion.div>
          </>
        )}

        {/* Disclaimer */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
          className="text-xs text-gray-400 text-center mb-4"
        >
          K-Beauty AI provides cosmetic guidance only. This is not a medical diagnosis.
          For skin diseases or medical concerns, consult a licensed dermatologist.
        </motion.p>

        {/* Affiliate Disclosure */}
        {/* Footer */}
        <motion.footer
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
          className="mt-12 pt-8 border-t border-gray-200 text-center"
        >
          {/* Contact */}
          <p className="text-sm text-gray-500 mb-3">
            Questions or concerns?{' '}
            <a
              href="mailto:contact@adayhealth.com"
              className="text-[#46C0C6] hover:underline"
            >
              contact@adayhealth.com
            </a>
          </p>

          {/* Links */}
          <div className="flex justify-center gap-4 text-xs text-gray-400 mb-4">
            <a
              href="https://adayinc.notion.site/K-Beauty-AI-Terms-Conditions-2f0e370464bc80408cf4c4f281da9843"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-gray-600 hover:underline"
            >
              Terms of Service
            </a>
            <span>•</span>
            <a
              href="https://adayinc.notion.site/K-Beauty-AI-Privacy-Policy-2f0e370464bc80d3bf48fe4b9fda2fec"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-gray-600 hover:underline"
            >
              Privacy Policy
            </a>
          </div>

          {/* Company Info */}
          <p className="text-xs text-gray-400 mb-2">
            © {new Date().getFullYear()} Aday Health, LLC
          </p>
          <p className="text-xs text-gray-300 mb-6">
            131 Continental Dr, Suite 305, Newark, DE 19713
          </p>

          {/* Test: Restart button - DEV ONLY */}
          <button
            onClick={async () => {
              // Sign out from Firebase first to clear auth session
              try {
                await firebaseSignOut()
              } catch (e) {
                console.error('Failed to sign out:', e)
              }
              // Clear all app data (localStorage stores + additional flags)
              localStorage.removeItem('k-skin-survey')
              localStorage.removeItem('k-skin-analysis')
              localStorage.removeItem('k-skin-auth')
              localStorage.removeItem('k-skin-full-survey')
              localStorage.removeItem('k-skin-report')
              localStorage.removeItem('k-skin-check-auth')
              localStorage.removeItem('k-skin-retake-used')
              localStorage.removeItem('k-skin-pending-redirect')
              sessionStorage.clear()
              // Force full page reload to clear in-memory Zustand state
              window.location.href = '/'
            }}
            className="text-xs text-gray-400 underline hover:text-gray-600 mb-4"
          >
            Test: Restart User Flow
          </button>
        </motion.footer>
      </main>

      {/* Metric Detail Sheet */}
      <MetricDetailSheet
        isOpen={isDetailOpen}
        onClose={() => setIsDetailOpen(false)}
        metric={selectedMetric}
        photoUrl={results.photoUrl}
        allMetrics={allMetricsArray}
        onNavigate={handleMetricNavigate}
      />
    </div>
  )
}

// Category icons for display
const CATEGORY_ICONS: Record<string, string> = {
  cleanser: '🧴',
  exfoliator: '✨',
  'wash-off-mask': '🧖',
  toner: '💧',
  essence: '💎',
  serum: '🧪',
  'sheet-mask': '🎭',
  'eye-cream': '👁️',
  moisturizer: '🧊',
  sunscreen: '☀️',
  'sleeping-mask': '🌙',
  'spot-treatment': '💊',
  'face-oil': '🫒',
}

// Recommended Product Card Component (new format)
function RecommendedProductCard({
  step,
  selectedTier,
  userConcerns,
  userSkinType,
}: {
  step: RecommendedStep
  selectedTier: PriceTier
  userConcerns?: UserConcerns | null
  userSkinType?: SurveySkinType | null
}) {
  const product = step.products[selectedTier]

  // If no product for this tier, show placeholder
  if (!product) {
    return (
      <div className="bg-gray-50 rounded-xl p-4 border border-gray-100 opacity-50">
        <div className="flex gap-4">
          <div className="w-16 h-16 rounded-lg bg-gray-200 flex-shrink-0 flex items-center justify-center">
            <span className="text-2xl">{CATEGORY_ICONS[step.category] || '📦'}</span>
          </div>
          <div className="flex-1">
            <p className="text-xs text-gray-400 font-medium uppercase">
              Step {step.step}: {step.label}
            </p>
            <p className="text-sm text-gray-400 mt-1">No {selectedTier} option available</p>
          </div>
        </div>
      </div>
    )
  }

  // Get affiliate link
  const affiliateLink = product.affiliateUrl || product.purchaseLinks?.amazon || ''

  // Get product tags (personalized based on user's concerns)
  const productTags = getProductTags(product, userConcerns, userSkinType)

  // Wrap entire card in link if affiliate URL exists
  const CardWrapper = affiliateLink ? (
    { children }: { children: React.ReactNode }
  ) => (
    <a
      href={affiliateLink}
      target="_blank"
      rel="noopener noreferrer"
      className="block bg-white/80 backdrop-blur-sm rounded-2xl p-4 shadow-sm border border-white/50 hover:shadow-md hover:bg-white transition-all cursor-pointer"
    >
      {children}
    </a>
  ) : (
    { children }: { children: React.ReactNode }
  ) => (
    <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 shadow-sm border border-white/50">
      {children}
    </div>
  )

  return (
    <CardWrapper>
      <div className="flex gap-4">
        {/* Product Image / Category Icon */}
        <div className="w-16 h-16 rounded-lg bg-gray-100 flex-shrink-0 flex items-center justify-center overflow-hidden">
          {product.imageUrl ? (
            <img src={product.imageUrl} alt={product.name} className="w-full h-full object-cover" />
          ) : (
            <span className="text-2xl">{CATEGORY_ICONS[step.category] || '📦'}</span>
          )}
        </div>

        {/* Product Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <p className="text-xs text-[#46C0C6] font-medium uppercase">
                Step {step.step}: {step.label}
              </p>
              <p className="text-sm font-semibold text-gray-900 line-clamp-2">{product.name}</p>
              <p className="text-sm text-gray-500">{product.brand}</p>
            </div>
            <p className="font-bold text-gray-900 flex-shrink-0">${product.price.toFixed(0)}</p>
          </div>

          {/* Product Tags (Concerns + Skin Type) */}
          {productTags.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {productTags.map((tag, i) => {
                const colors = tag.concernKey ? CONCERN_COLORS[tag.concernKey] : null
                return (
                  <span
                    key={i}
                    className={`text-[10px] px-2 py-0.5 rounded-full ${
                      tag.type === 'skinType'
                        ? 'bg-[#46C0C6]/20 text-[#46C0C6]'
                        : colors
                          ? `${colors.bg} ${colors.text}`
                          : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    #{tag.label}
                  </span>
                )
              })}
            </div>
          )}

          {/* Product Benefit */}
          {product.benefit && (
            <p className="text-xs text-gray-500 mt-1 line-clamp-3">{product.benefit}</p>
          )}

          {/* Key Ingredients */}
          {product.keyIngredients.length > 0 && (
            <p className="text-xs text-gray-400 mt-1 line-clamp-1">
              {product.keyIngredients.slice(0, 3).join(' • ')}
            </p>
          )}

          {/* Shop Link indicator */}
          {affiliateLink && (
            <p className="inline-flex items-center gap-1 text-xs text-[#46C0C6] font-medium mt-2">
              Shop on Amazon
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </p>
          )}
        </div>
      </div>
    </CardWrapper>
  )
}
